import MkReportSDK from '@markting/mk-report-sdk';
import { createAdId } from '@markting/mk-create-h5-url';
import { post, webGateway } from '@/ajax';
import { getCurExternalContactAndChat } from '@/hooks/useCurExternal';

export type TMaterialType = 'article' | 'video-markting' | 'form' | 'poster' | 'link' | 'brochure' | 'friend' | 'H5' | 'small-routine'

/**
 * 营销素材——销售分享行为上报(上报后的数据进入素材的数据统计——例如上级分享人等数据)
 * @param id 素材ID
 * @param type 素材类型
 * @param saleShareId 发送前生成的分享id，发送出去后会携带该id，可根据此id查询上一步的分享人（必须存在才能关联）
 */
export const reportSaleShareBehavior = async (params: any) => {
  const { account, id, type, saleShareId, uid } = params;
  await post('/biz/v1/clue/sale_share/record', {
    companyId: account,
    operateEntity: 'material',
    operateEntityObj: {
      operateEntityId: id,
      operateEntityType: type
    },
    saleShareId,
    shareType: 'friend', // 分享到哪里,friend：好友，friendCircle：朋友圈
    userId: uid
  });
};

/**
 * 发送次数上报(营销素材/企业文件)
 * @param id 素材/文件ID
 * @param type 素材类型/ordinary（企业文件写死）
 */
export const reportSendTimes = async (id: string, type: TMaterialType | 'ordinary') => {
  await post('/biz/v2/material/inc/send_count', {
    materialId: id,
    materialType: type
  });
};

/**
 * 发送触达上报（内置获取外部联系人或群聊ID的逻辑）
 * 1:员工  2:线索  3:非微生态客户  4:群  5、外部联系人 6:企微朋友圈 7：个微朋友圈 8:c端客户
 * ⚠️注意：reportParams中还存在一个activityId，侧边栏发送时不传或者传入空字符串
 */
export const reportSendReach = async (params: any) => {
  const { account, adId, type } = params;
  const reportParams = { account, adId };

  // 获取外部联系人或群聊数据
  const curExternal = await getCurExternalContactAndChat();

  Object.assign(reportParams, {
    targetType: type ? type : (curExternal?.type === 'contact' ? '5' : '4'),
    targetId: curExternal?.id ? [curExternal.id] : undefined
  });

  await MkReportSDK().mkAddCollection(reportParams, webGateway);
};

/**
 * 触达模型上报(参数由外部控制，中间无其他处理逻辑)
 * @param reportParams
 */
export const reportReach = async (reportParams: any) => {
  await MkReportSDK().mkAddCollection(reportParams, webGateway);
};

/**
 * 针对营销素材的触达上报
 * @param id 素材ID
 * @param type 素材类型
 * @param adId 投放ID
 * ⚠️注意：
 *  1. 发送时生成，生成后发送出去的链接及上报都必须保持该ID的一致性，如果直接调用的createMaterialUrl，该方法会返回adId
 *  2. 针对海报、小程序单页、朋友圈素材而言，则需单独生成adId，可以选择在外部生成后传入也可以选择不传入，不传入则会在内部生成
 *  3. 针对文章、宣传册、H5、表单、任意链接、营销视频而言，则必须传入外部生成的adId以保证其在C端上报时的关联性
 *  4. 针对文章、宣传册、H5、表单、任意链接、营销视频，进行分享朋友圈操作也必须传入外部生成的adId
 *
 *  落地页仅支持文章、宣传册、H5、表单、任意链接、营销视频，其余素材类型不存在落地页
 */
export const reportMaterialSendReach = async (params: any) => {
  const { account, adId, type } = params;
  // 文章、宣传册、H5、表单、任意链接、营销视频
  if (['article', 'brochure', 'H5', 'form', 'link', 'video-markting'].includes(type)) {
    adId && await reportSendReach({ account, adId });
    return;
  }
};

/**
 * 针对企业文件的触达上报
 * @param id 文件ID
 * ⚠️注意：因为企业文件需要单独生成adId，所以需要先调用createAdId，再调用reportSendReach，而素材在发送前就已经生成adId，所以直接调用reportSendReach即可
 */
export const reportOrdinarySendReach = async (params: any) => {
  const { uid, account, id } = params;
  const { success, data } = await createAdId({
    account,
    extraData: {
      relationId: id,
      relationType: 'ordinary',
      deliverType: 'staffShare',
      shareType: 'staff',
      shareId: uid
    },
    sceneType: 'sidebar-H5'
  });
  if (success) await reportSendReach({
    account,
    adId: data.adId
  });
};

/**
 * 营销素材——上报逻辑汇总
 * @param id 素材id
 * @param materialType 素材类型
 * @param otherParams
 */
export const reportMaterial = (params: any) => {
  const { id, type } = params;
  // 销售行为分享上报——关联上级分享人
  reportSaleShareBehavior(params);
  // 触达上报
  reportMaterialSendReach(params);
  // 发送次数
  reportSendTimes(id, type);
};

/**
 * 企业文件——上报逻辑汇总
 * @param id 企业文件id
 */
export const reportOrdinary = (params: any) => {
  // 触达上报
  reportOrdinarySendReach(params);
  // 发送次数
  reportSendTimes(params.id, 'ordinary');
};
